'use client';

import { useEffect, useState } from 'react';
import { COST, isOpenEnded, type CostRange } from './cost-sources';
import { nb } from '@/lib/nobreak';

/**
 * "The flip" — cost per LEAD on the left axis, cost per FUNDED LOAN on the
 * right, one band per lead source. The shared band starts near the bottom and
 * finishes at the top; yours finishes at the bottom. That crossing is the
 * point of the whole cost table, and a table of eleven rows never showed it.
 *
 * Figures are the ranges already published in the table below it
 * (LEAD-COST-ECONOMICS-SOURCE-OF-TRUTH.md). Zillow is omitted because its
 * published range is "varies". Open-ended ranges ($10,000+, $2,000+, $150+)
 * are drawn to a fading edge rather than given an invented ceiling.
 */

type Src = {
  key: string; name: string; sub: string;
  cpl: [number, number]; cpfl: [number, number];
  cplLabel: string; cpflLabel: string; open?: boolean; ours?: boolean; color: string;
};

/** A drawn band needs a top; an open-ended range gets a short one and a fading edge, not a claim. */
const ranged = (r: CostRange) => ({
  cpl: r.cpl, cplLabel: r.cplLabel, cpflLabel: r.cpflLabel, open: isOpenEnded(r),
  cpfl: [r.cpfl[0], r.cpfl[1] ?? r.cpfl[0] * 1.125] as [number, number],
});

const SOURCES: Src[] = [
  { key: 'shared', name: 'Fresh shared leads', sub: 'LendingTree-type, up to 5 buyers', ...ranged(COST.shared), color: '#9A9DA8' },
  { key: 'bankrate', name: 'Bankrate rate table', sub: 'Shared, fewer buyers', ...ranged(COST.rate), color: '#6F7385' },
  { key: 'vendor', name: 'Vendor “exclusive”', sub: 'Sold to one buyer', ...ranged(COST.vendor), color: '#C3C5CD' },
  { key: 'ours', name: 'Generate your own', sub: 'First-party exclusive · purchase', ...ranged(COST.ours), ours: true, color: '#ff34a7' },
];

const W = 1000, H = 520, TOP = 70, BOT = 470, XL = 150, XR = 660;
const logY = (v: number, lo: number, hi: number) => BOT - ((Math.log(v) - Math.log(lo)) / (Math.log(hi) - Math.log(lo))) * (BOT - TOP);
const yL = (v: number) => logY(v, 8, 320);
const yR = (v: number) => logY(v, 1000, 12500);

function band(s: Src) {
  const [a, b] = s.cpl, [c, d] = s.cpfl;
  const m = (XL + XR) / 2;
  return `M${XL},${yL(b)} C${m},${yL(b)} ${m},${yR(d)} ${XR},${yR(d)} L${XR},${yR(c)} C${m},${yR(c)} ${m},${yL(a)} ${XL},${yL(a)} Z`;
}

/**
 * One layout in the DOM at a time. Server HTML (what crawlers read) is the full
 * chart; narrow screens swap to paired bars after hydration. Shipping both and
 * hiding one with CSS put every label in the page twice.
 */
function useNarrow() {
  const [narrow, setNarrow] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const on = () => setNarrow(mq.matches);
    on(); mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return narrow;
}

export function LPCostFlip() {
  const [active, setActive] = useState<string | null>(null);
  const narrow = useNarrow();
  const on = (k: string) => active === null || active === k;
  const focus = SOURCES.find((s) => s.key === (active ?? 'ours'))!;

  return (
    <div className="overflow-hidden rounded-[32px] bg-white text-[#0B0C12] ring-1 ring-black/[.08] shadow-[0_40px_100px_-60px_rgba(11,12,18,.5)]">
      <div className="flex flex-col gap-6 border-b border-black/[.06] p-6 sm:p-9 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="lp-eyebrow text-[#c8117f]">The flip</p>
          <p className="lp-display mt-3 text-[2rem] font-semibold leading-[1.05] sm:text-[2.6rem]">
            Cheapest per lead. <span className="lp-grad">Most expensive per loan.</span>
          </p>
          <p className="mt-3 max-w-[36rem] text-[1rem] text-[#5A5E69]">Follow each lead source from what it costs to buy, on the left, to what it costs to actually fund a loan, on the right.</p>
        </div>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Highlight a lead source">
          {SOURCES.map((s) => (
            <button key={s.key} type="button"
              onMouseEnter={() => setActive(s.key)} onMouseLeave={() => setActive(null)}
              onFocus={() => setActive(s.key)} onBlur={() => setActive(null)}
              onClick={() => setActive(active === s.key ? null : s.key)}
              className={`inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-[13px] font-medium transition ${active === s.key ? 'bg-[#0B0C12] text-white' : 'bg-[#F3F3F1] text-[#3B3F4A] hover:bg-[#EAEAE6]'}`}>
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: s.ours ? 'linear-gradient(135deg,#ff34a7,#294bfd)' : s.color }} />
              {s.name}
            </button>
          ))}
        </div>
      </div>

      {!narrow ? (
      <div className="px-4 pb-4 pt-2">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img"
          aria-label="Chart: shared leads have the lowest cost per lead but the highest cost per funded loan; first-party exclusive leads have the lowest cost per funded loan.">
          <defs>
            <linearGradient id="lpflip-ours" x1="0" x2="1"><stop stopColor="#ff34a7" /><stop offset=".55" stopColor="#b43cf0" /><stop offset="1" stopColor="#294bfd" /></linearGradient>
            <linearGradient id="lpflip-fade" x1="0" y1="0" x2="0" y2="1"><stop stopColor="#fff" stopOpacity="1" /><stop offset="1" stopColor="#fff" stopOpacity="0" /></linearGradient>
          </defs>

          {/* axes */}
          {[[XL, 'COST PER LEAD', [10, 30, 100, 300], yL, (v: number) => `$${v}`] as const, [XR, 'COST PER FUNDED LOAN', [1000, 2000, 5000, 10000], yR, (v: number) => `$${v / 1000}K`] as const].map(([x, title, ticks, y, f], i) => (
            <g key={title}>
              <text x={x} y={34} textAnchor="middle" fontSize="13" fontWeight="700" letterSpacing="2" fill="#6B6F7A">{title}</text>
              <line x1={x} x2={x} y1={TOP - 14} y2={BOT + 10} stroke="#E4E4E0" strokeWidth="2" />
              {ticks.map((t) => (
                <g key={t}>
                  <line x1={x - 6} x2={x + 6} y1={y(t)} y2={y(t)} stroke="#D2D2CD" strokeWidth="2" />
                  <text x={i === 0 ? x - 16 : x + 16} y={y(t) + 5} textAnchor={i === 0 ? 'end' : 'start'} fontSize="15" fontWeight="600" fill="#6B6F7A">{f(t)}</text>
                </g>
              ))}
            </g>
          ))}
          <text x={(XL + XR) / 2} y={BOT + 40} textAnchor="middle" fontSize="13" fill="#A2A5AE">cheap ↓ · expensive ↑ (log scale)</text>

          {/* bands: others first, ours on top */}
          {SOURCES.map((s) => (
            <path key={s.key} d={band(s)}
              fill={s.ours ? 'url(#lpflip-ours)' : s.color}
              opacity={on(s.key) ? (s.ours ? 0.92 : 0.5) : 0.08}
              style={{ transition: 'opacity .25s ease' }}
              onMouseEnter={() => setActive(s.key)} onMouseLeave={() => setActive(null)} />
          ))}
          {/* open-ended tops fade instead of stopping at an invented ceiling */}
          <rect x={XR - 40} y={TOP - 20} width="44" height={yR(COST.shared.cpfl[1] as number) - TOP + 22} fill="url(#lpflip-fade)" opacity={on('shared') ? 1 : 0} />

          {/* right-hand labels */}
          {SOURCES.map((s) => {
            const y = yR(Math.sqrt(s.cpfl[0] * s.cpfl[1]));
            return (
              <g key={s.key} opacity={on(s.key) ? 1 : 0.2} style={{ transition: 'opacity .25s ease' }}>
                <circle cx={XR} cy={y} r="5" fill={s.ours ? '#ff34a7' : s.color} />
                <text x={XR + 64} y={y - 4} fontSize="16" fontWeight="700" fill="#0B0C12">{s.name}</text>
                <text x={XR + 64} y={y + 16} fontSize="14" fontWeight="600" fill={s.ours ? '#c8117f' : '#6B6F7A'}>{s.cpflLabel} per loan</text>
              </g>
            );
          })}
        </svg>
      </div>

      ) : (
      <div className="space-y-5 p-6">
        {SOURCES.map((s) => {
          const pctL = ((Math.log(s.cpl[1]) - Math.log(8)) / (Math.log(320) - Math.log(8))) * 100;
          const pctR = ((Math.log(s.cpfl[1]) - Math.log(1000)) / (Math.log(12500) - Math.log(1000))) * 100;
          return (
            <div key={s.key} className={`rounded-2xl p-4 ${s.ours ? 'bg-[#FFF5FA] ring-1 ring-[#ff34a7]/30' : 'bg-[#F6F5F1]'}`}>
              <p className="font-semibold">{s.name}</p>
              <p className="text-[12px] text-[#6B6F7A]">{s.sub}</p>
              {[['Per lead', s.cplLabel, pctL], ['Per funded loan', s.cpflLabel, pctR]].map(([l, v, p]) => (
                <div key={l as string} className="mt-3">
                  <div className="flex justify-between text-[12px]"><span className="text-[#6B6F7A]">{l}</span><span className="font-semibold">{v}</span></div>
                  <div className="mt-1 h-2 rounded-full bg-black/[.06]"><div className="h-full rounded-full" style={{ width: `${Math.max(6, p as number)}%`, background: s.ours ? 'linear-gradient(90deg,#ff34a7,#294bfd)' : s.color }} /></div>
                </div>
              ))}
            </div>
          );
        })}
      </div>
      )}

      {/* focus readout */}
      <div className="lp-flip-read grid gap-px border-t border-black/[.06] bg-black/[.06] sm:grid-cols-3">
        {[['Source', `${focus.name}`, focus.sub], ['Cost per lead', focus.cplLabel, 'what the invoice says'], ['Cost per funded loan', focus.cpflLabel, 'what a closed loan actually costs']].map(([k, v, n], i) => (
          <div key={k} className="bg-white text-[#0B0C12] px-6 py-5 sm:px-8 [container-type:inline-size]">
            <p className="text-[12px] uppercase tracking-[.14em] text-[#6B6F7A]">{k}</p>
            <p className={`mt-1.5 ${i === 0 ? 'lp-display-sm text-[1.2rem] font-semibold' : 'lp-num whitespace-nowrap text-[min(1.9rem,13cqi)]'} ${i === 2 && focus.ours ? 'lp-grad' : ''}`}>{nb(v)}</p>
            <p className="text-[13px] text-[#6B6F7A]">{n}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
